import cn from 'classnames'
import { useTheme } from 'next-themes'
import React, { FC } from 'react'

import LoadingAnimation from '@/shared/LoadingAnimation'
import { mapButtonTypeToAnimationColor } from 'utils/animationUtil'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary'

/**
 * Variant styling, moved out of _btn.scss (P7).
 *
 * Written out in full per variant rather than composed, because Tailwind's
 * scanner only sees LITERAL class strings in the source. The variant class
 * itself is built by interpolation (`${variant}-button`), which the scanner
 * cannot resolve — so utilities assembled the same way would be purged from the
 * bundle with no build error.
 *
 * The disabled row is a separate string, not an override: two colour utilities
 * of equal specificity are resolved by their order in the generated stylesheet,
 * not by the order they appear in a className, so only one may ever be present.
 *
 * `[&_svg_path]:stroke-*` reproduces `.tertiary-button .icon-container svg path`
 * exactly — the same property on the same element. `text-*` would be wrong here:
 * it sets `color`, which is the initial value of `outline-color`,
 * `border-*-color` and `text-decoration-color` too.
 *
 * `primary`'s icon strokes spell out `.icon-container` (0-2-2) and, on the
 * disabled row, `.disabled` (0-3-2, so the 0-2-2 one is not repeated there). The
 * enabled row draws in `on-primary`, which a branded console sets to a colour its
 * primary is contrast-checked against. Page-level icon rules (the home top bar,
 * sub-organizations) are balanced against exactly those weights.
 *
 * `border-solid` is not redundant: the base rule sets `border: none`, so a bare
 * `border` utility would set a width on a style of `none` and paint nothing.
 *
 * Both of those depend on the base rule now living in `@layer base` (see
 * tailwind/base.js). Before that move it was emitted after the utility layer, where
 * `.safari-button` — a CLASS — tied with `px-0` and `border-solid` at 0-1-0 and
 * won on source order. `!important` settles the tie but also beats the
 * page-level rules that legitimately set padding on 21 of these buttons.
 */
const VARIANT_CLASSES: Partial<Record<ButtonVariant, { base: string, disabled: string }>> = {
  tertiary: {
    base: 'bg-transparent-color px-0 text-gray-1 hover:text-gray-1 active:text-gray-1 [&_svg_path]:stroke-gray-1',
    disabled: 'bg-transparent-color px-0 text-gray-4 hover:text-gray-4 [&_svg_path]:stroke-gray-4',
  },
  primary: {
    base: 'bg-primary-main text-on-primary hover:bg-primary-hover active:bg-primary-main [&_.icon-container_svg_path]:stroke-on-primary',
    disabled: 'bg-gray-4 text-gray-7 hover:bg-gray-5 active:bg-gray-5 [&.disabled_.icon-container_svg_path]:stroke-gray-7 [&.disabled_.icon-container_svg_path]:[stroke-opacity:1]',
  },
  secondary: {
    base: 'border border-solid border-gray-1 bg-transparent-color text-gray-1 hover:bg-gray-5 active:bg-transparent-color [&_svg_path]:stroke-gray-1',
    disabled: 'border border-solid border-gray-4 bg-transparent-color text-gray-4 hover:bg-transparent-color active:bg-transparent-color [&_svg_path]:stroke-gray-4',
  },
}

// The icon slot. Its margin flips with `iconLeft`, and the two margin sets are
// exclusive for the same reason as the disabled row above.
const ICON_CONTAINER = 'flex items-center justify-center h-[16px] w-[16px]'
const ICON_MARGIN_RIGHT = 'ml-[8px]'
const ICON_MARGIN_LEFT = 'ml-0 mr-[8px]'

export type ButtonType = 'submit' | 'button'

type GrainButtonProps = {
  variant: ButtonVariant,
  text?: React.ReactNode,
  type?: ButtonType,
  isDisabled?: boolean,
  isOnSecondary?: boolean,
  isOnPrimary?: boolean,
  isSafari?: boolean,
  isLoading?: boolean,
  iconLeft?: boolean,
  fullWidth?: boolean,
  onClick?: React.MouseEventHandler<HTMLElement>,
  onBlur?: () => void,
  icon?: React.ReactElement,
  className?: string,
  children?: React.ReactNode,
  iconContainerClassName?: string,
}

const GrainButton : FC<GrainButtonProps> = ({
  icon,
  text,
  type = 'button',
  onBlur = () => false,
  variant,
  onClick = () => false,
  iconLeft = false,
  isSafari = false,
  isLoading = false,
  fullWidth = false,
  className = '',
  isDisabled = false,
  isOnPrimary = false,
  isOnSecondary = false,
  children,
  iconContainerClassName = '',
}) => {
  const { theme } = useTheme()
  const variantClasses = VARIANT_CLASSES[variant]
  const buttonClassnames = cn(
    `${variant}-button`,
    variantClasses && (isDisabled ? variantClasses.disabled : variantClasses.base),
    { [`${className}`]: !!className },
    { loading: isLoading },
    { disabled: isDisabled },
    { 'icon-left': iconLeft },
    { 'full-width': fullWidth },
    { 'on-primary': isOnPrimary },
    { 'safari-button': isSafari },
    { 'on-secondary': isOnSecondary },
    // The shadow is an arbitrary property on purpose: Tailwind's named "none"
    // shadow composes three transparent `--tw-*` layers, which is not `none`.
    { 'pointer-events-none [box-shadow:none]': isDisabled },
    { 'flex-row-reverse': iconLeft },
    { 'w-full': fullWidth },
  )
  const iconContainerClassnames = cn(
    'icon-container',
    ICON_CONTAINER,
    iconLeft ? ICON_MARGIN_LEFT : ICON_MARGIN_RIGHT,
  )

  const loadingAnimationColor = mapButtonTypeToAnimationColor(variant, theme === 'dark')

  if (isSafari) {
    return (
      <div
        tabIndex={0}
        role="button"
        onClick={onClick}
        onBlur={onBlur}
        className={buttonClassnames}
      >
        { isLoading
          ? <LoadingAnimation />
          : (
            <>
              { text && <span>{ text }</span>}
              { icon && <span className={cn(iconContainerClassnames, iconContainerClassName)}>{ icon }</span> }
            </>
          )}
      </div>
    )
  }

  /* eslint-disable react/button-has-type */
  return (
    <button
      type={type}
      onClick={onClick}
      onBlur={onBlur}
      disabled={isDisabled || isLoading}
      className={buttonClassnames}
    >
      { isLoading
        ? <LoadingAnimation color={loadingAnimationColor} />
        : (
          <>
            { children || (
              <>
                {text && <span>{ text }</span>}
                {icon && <span className={iconContainerClassnames}>{ icon }</span> }
              </>
            )}
          </>
        )}
    </button>
  )
  /* eslint-enable react/button-has-type */
}

export default GrainButton
